import React, { useState } from 'react';
import { Box, Typography, Stack, Avatar, IconButton, List, ListItem, ListItemAvatar, ButtonBase, Skeleton, Button, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import { format } from 'date-fns';
import { ChevronLeft, Close, Add, Delete } from '@mui/icons-material';

import { APP_CONFIG } from '@config/app.config';

import { PurchaseOrderItem } from '@domain/purchasing';

import ProductDetailsDrawer from '@features/products/components/detail/ProductDetailsDrawer';
import ShipmentActions from './ShipmentActions';
import { useGenericDrawer } from '@shared/hooks';
import ShipmentPreview from '@features/purchasing/components/preview/ShipmentPreview';
import { useShipment, useShipments } from '../../hooks/useShipments';
import { BorderedBlock } from '@shared/ui/common/BorderedBlock';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { CreateShipmentItems } from '@features/shipments/components/create/CreateShipmentItems';
import { Icon } from '@shared/icons';


interface ShipmentDetailDrawerProps {
  shipmentId: string;
  open?: boolean;
  onClose?: () => void;
  isDeleting?: boolean;
}

const ShipmentDetailDrawer: React.FC<ShipmentDetailDrawerProps> = ({
  shipmentId,
  onClose
}) => {

  const { shipment, isLoading } = useShipment(shipmentId);
  const { addPurchaseOrderItemsToShipment, removePurchaseOrderItemsFromShipment } = useShipments();
  const { openBottomSheet, closeBottomSheet } = useBottomSheet();

  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState<PurchaseOrderItem | null>(null);

  const { openDrawer, onBack } = useGenericDrawer();

  const handlePurchaseOrderItemClick = (purchaseOrderItem: PurchaseOrderItem) => {
    openDrawer(
      `Product ${purchaseOrderItem.product.title || ''}`,
      <ProductDetailsDrawer productId={purchaseOrderItem.product.id} />,
      () => openDrawer('Shipment Details', <ShipmentDetailDrawer shipmentId={shipmentId} onClose={onClose} />)
    );
  };

  const handleAddProducts = () => {
    openBottomSheet({
      component: (
        <CreateShipmentItems
          onNext={async (selectedItems) => {
            if (shipment?.id) {
              try {
                await addPurchaseOrderItemsToShipment({
                  id: shipment.id,
                  request: {
                    purchaseOrderItemIds: selectedItems.map(item => item.id || '')
                  }
                });

                closeBottomSheet();
                // The shipment data will be refreshed automatically by React Query
              } catch (error) {
                console.error('Error adding products to shipment:', error);
              }
            }
          }}
          onClose={closeBottomSheet}
          purchaseOrderItems={shipment?.purchaseOrderItems || []}
        />
      ),
      title: 'Add Products to Shipment',
      closeOnBackdropClick: true,
    });
  };

  const handleRemoveItem = (item: PurchaseOrderItem) => {
    setItemToRemove(item);
    setRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (itemToRemove && shipment?.id) {
      try {
        await removePurchaseOrderItemsFromShipment({
          id: shipment.id,
          request: {
            purchaseOrderItemIds: [itemToRemove.id || '']
          }
        });
        setRemoveDialogOpen(false);
        setItemToRemove(null);
        // The shipment data will be refreshed automatically by React Query
      } catch (error) {
        console.error('Error removing product from shipment:', error);
      }
    }
  };

  const handleCancelRemove = () => {
    setRemoveDialogOpen(false);
    setItemToRemove(null);
  };

  return (
    <Stack sx={{ flexGrow: 1, p: 3, gap: 3 }}>

      {isLoading && (
        <Stack direction="column" justifyContent="center" alignItems="center" sx={{ flexGrow: 1 }} spacing={2}>
          <Skeleton variant="rounded" width="100%" height={40} />
          <Skeleton variant="rounded" width="100%" height={60} />
          <Skeleton variant="rounded" width="100%" height={100} />
          <Skeleton variant="rounded" width="100%" height={150} />
        </Stack>
      )}



      {/* Shipment Details Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" >
        <Stack direction="row" alignItems="center" spacing={0}>
          {onBack && (
            <IconButton onClick={onBack} edge="start">
              <ChevronLeft />
            </IconButton>
          )}
          <Typography variant="h6">Shipment Details</Typography>
        </Stack>

        <Stack direction="row" spacing={1}>
          {shipment && <ShipmentActions shipment={shipment} />}
          {onClose && (
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {/* Shipment summary */}

      <BorderedBlock>
        {shipment && <ShipmentPreview shipment={shipment} />}
      </BorderedBlock>

      {/* Items Section */}
      <BorderedBlock sx={{ p: 0 }} header={
        <Stack direction="row" sx={{ justifyContent: 'space-between' }} spacing={2}>
          <Typography variant="subtitle2" color="text.primary">
            Products ({shipment?.purchaseOrderItems.length || 0})
          </Typography>
          <Typography variant="subtitle2" color="text.primary">
            Items ({shipment?.purchaseOrderItems.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0})
          </Typography>
        </Stack>
      }>

        <List sx={{ p: 0, }}>
          {shipment?.purchaseOrderItems.length === 0 && (
            <ListItem sx={{ justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                This shipment is empty
              </Typography>
            </ListItem>
          )}

          {shipment?.purchaseOrderItems.map((item, index) => (
            <ListItem
              divider={index !== shipment?.purchaseOrderItems.length - 1}
              key={`${item.product.id}-${index}`}
              sx={{
                gap: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                '& .unlink-button': {
                  display: 'none'
                },
                '&:hover .unlink-button': {
                  display: 'inline-flex'
                }
              }}
            >
              <ButtonBase
                onClick={() => handlePurchaseOrderItemClick(item)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flex: 1,
                  textAlign: 'left'
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={item.product.image}
                    variant="rounded"
                    sx={{
                      width: 50,
                      height: 50,
                      border: 1,
                      borderColor: APP_CONFIG.ui.theme.colors.border
                    }}
                  />
                </ListItemAvatar>

                <Stack direction="column" spacing={0} sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'normal',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      display: '-webkit-box'
                    }}
                  >
                    {item.product.title || 'Untitled Product'}
                  </Typography>
                </Stack>

                <Box>
                  <Typography variant="subtitle2" noWrap color="text.primary">
                    {item.quantity || 0} items
                  </Typography>
                </Box>
              </ButtonBase>

              <IconButton
                className='unlink-button'
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveItem(item);
                }}
                size="small"
              >
                <Icon icon="LinkBroken" />
              </IconButton>
            </ListItem>
          ))}
        </List>

        {/* Add Products Button */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="text"
            color="secondary"
            startIcon={<Add />}
            onClick={handleAddProducts}
          >
            Add Purchase Order Items
          </Button>
        </Box>
      </BorderedBlock>


      {/* Shipment Information */}


      <BorderedBlock header="Shipment Information">
        <Stack spacing={2} >

          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
              Transport:
            </Typography>
            <Typography variant="body2">
              {shipment?.transportMethod}
            </Typography>
          </Stack>


          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
              Departure Date:
            </Typography>
            <Typography variant="body2">
              {shipment?.departureDate ? format(shipment.departureDate, 'EEEE, MMMM dd, yyyy') : 'Not set'}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
              Arrival Date:
            </Typography>

            <Typography variant="body2">
              {shipment?.arrivalDate ? format(shipment.arrivalDate, 'EEEE, MMMM dd, yyyy') : 'Not set'}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 120 }}>
              Processed Date:
            </Typography>

            <Typography variant="body2">
              {shipment?.processedDate ? format(shipment.processedDate, 'EEEE, MMMM dd, yyyy') : 'Not set'}
            </Typography>
          </Stack>
        </Stack>
      </BorderedBlock>

      {/* Remove Confirmation Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={handleCancelRemove}
        aria-labelledby="remove-dialog-title"
        aria-describedby="remove-dialog-description"
      >
        <DialogTitle id="remove-dialog-title">
          Remove Product from Shipment
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="remove-dialog-description">
            Are you sure you want to remove "{itemToRemove?.product.title || 'this product'}" from the shipment?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmRemove} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

    </Stack >

  );
};

export default ShipmentDetailDrawer; 